import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faPencilAlt, 
  faTrash, 
  faSave, 
  faTimes, 
  faEye, 
  faEyeSlash,
  faCog,
  faRobot,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';

const normalizeOpenAIModelId = (modelId) => {
  const normalized = String(modelId || '').trim().toLowerCase();
  if (!normalized) {
    return '';
  }

  const segments = normalized.split('/').map((segment) => segment.trim()).filter(Boolean);
  const lastSegment = segments.length > 0 ? segments[segments.length - 1] : normalized;
  if (!lastSegment.startsWith('ft:')) {
    return lastSegment;
  }

  const ftParts = lastSegment.split(':').map((part) => part.trim()).filter(Boolean);
  return ftParts.length > 1 ? ftParts[1] : lastSegment;
};

const isOpenAIGPT5Family = (modelId) => /^gpt-5(?:[.-]|$)/.test(normalizeOpenAIModelId(modelId));
const isOpenAIOSeriesReasoningModel = (modelId) => /^o\d+(?:[.-]|$)/.test(normalizeOpenAIModelId(modelId));
const supportsOpenAIXHighReasoningEffort = (modelId) => {
  const normalizedModelId = normalizeOpenAIModelId(modelId);
  const match = normalizedModelId.match(/^gpt-5\.(\d+)(?:[.-]|$)/);
  if (!match) {
    return false;
  }

  return Number(match[1]) > 1;
};
const supportsOpenAIResponsesControls = (modelId) => isOpenAIGPT5Family(modelId) || isOpenAIOSeriesReasoningModel(modelId);
const supportsOpenAIVerbosity = (modelId) => isOpenAIGPT5Family(modelId);
const getAllowedOpenAIReasoningEfforts = (modelId) => {
  const normalizedModelId = normalizeOpenAIModelId(modelId);
  if (!normalizedModelId) {
    return [];
  }

  if (/^gpt-5-pro(?:[.-]|$)/.test(normalizedModelId)) {
    return ['high'];
  }

  if (/^gpt-5\.1(?:[.-]|$)/.test(normalizedModelId)) {
    return ['none', 'low', 'medium', 'high'];
  }

  if (isOpenAIGPT5Family(normalizedModelId)) {
    const efforts = ['none', 'low', 'medium', 'high'];
    if (supportsOpenAIXHighReasoningEffort(normalizedModelId)) {
      efforts.push('xhigh');
    }
    return efforts;
  }

  if (isOpenAIOSeriesReasoningModel(normalizedModelId)) {
    return ['low', 'medium', 'high'];
  }

  return [];
};
const supportsOpenAIReasoningEffort = (modelId, effort) => getAllowedOpenAIReasoningEfforts(modelId).includes(String(effort || '').trim().toLowerCase());

const ApiModels = () => {
  const [models, setModels] = useState([]);
  const [systemModels, setSystemModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [activeTab, setActiveTab] = useState('user'); // 'user' или 'system'
  
  // Начальные значения формы
  const initialFormData = {
    name: '',
    provider: 'openai',
    modelId: '',
    apiKey: '',
    baseUrl: '',
    description: '',
    active: true,
    maxTokens: 2000,
    transportMode: 'auto',
    reasoningEffort: '',
    verbosity: '',
    isSystem: false,
    category: 'chat',
    siteUrl: '',
    siteName: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);
  const [showAddForm, setShowAddForm] = useState(false);
  const formRef = useRef(null);
  
  useEffect(() => {
    fetchModels();
    fetchSystemModels();
  }, []);
  
  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/models');
      setModels(response.data);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке моделей:', err);
      const errorMessage = err.response?.data?.message || 'Ошибка соединения с сервером';
      setError(`Не удалось загрузить модели API: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemModels = async () => {
    try {
      const response = await axios.get('/api/models/system');
      setSystemModels(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке системных моделей:', err);
    }
  };
  
  const resetForm = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setCurrentId(null);
    setShowAddForm(false);
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let updates = { [name]: type === 'checkbox' ? checked : value };
    const nextProvider = name === 'provider' ? value : formData.provider;
    const nextTransportMode = name === 'transportMode' ? value : (formData.transportMode || 'auto');
    const nextModelId = name === 'modelId' ? value : formData.modelId;
    
    // Автоматические настройки для провайдеров
    if (name === 'provider') {
      switch (value) {
        case 'openrouter':
          updates.baseUrl = 'https://openrouter.ai/api/v1';
          updates.description = 'OpenRouter - Доступ к множеству моделей ИИ';
          break;
        case 'deepseek':
          updates.description = 'DeepSeek API - https://platform.deepseek.com/';
          updates.modelId = 'deepseek-chat';
          updates.baseUrl = '';
          break;
        case 'anthropic':
          updates.description = 'Anthropic Claude API';
          updates.baseUrl = '';
          break;
        case 'openai':
          updates.baseUrl = '';
          break;
      }

      if (value !== 'openai') {
        updates.transportMode = 'auto';
        updates.reasoningEffort = '';
        updates.verbosity = '';
      }
    }

    if (name === 'transportMode' && value === 'chat_completions') {
      updates.reasoningEffort = '';
      updates.verbosity = '';
    }

    if (
      nextProvider === 'openai' &&
      nextTransportMode !== 'chat_completions' &&
      !supportsOpenAIResponsesControls(nextModelId)
    ) {
      updates.reasoningEffort = '';
      updates.verbosity = '';
    }

    const nextReasoningEffort = name === 'reasoningEffort' ? value : (updates.reasoningEffort !== undefined ? updates.reasoningEffort : formData.reasoningEffort);
    if (nextReasoningEffort && !supportsOpenAIReasoningEffort(nextModelId, nextReasoningEffort)) {
      updates.reasoningEffort = '';
    }

    const nextVerbosity = name === 'verbosity' ? value : (updates.verbosity !== undefined ? updates.verbosity : formData.verbosity);
    if (nextVerbosity && !supportsOpenAIVerbosity(nextModelId)) {
      updates.verbosity = '';
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      let response;
      
      if (isEditing) {
        response = await axios.put(`/api/models/${currentId}`, formData);
        setModels(models.map(model => 
          model._id === currentId ? response.data : model
        ));
        toast.success('Модель успешно обновлена');
      } else {
        response = await axios.post('/api/models', formData);
        setModels([response.data, ...models]);
        toast.success('Модель успешно создана');
      }
      
      resetForm();
      
      // Обновляем системные модели если создали/изменили системную модель
      if (formData.isSystem) {
        fetchSystemModels();
      }
    } catch (err) {
      console.error('Ошибка при сохранении модели:', err);
      const errorMessage = err.response?.data?.message || 'Неизвестная ошибка';
      setError(`Не удалось сохранить модель: ${errorMessage}`);
      toast.error(`Ошибка: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditClick = async (model) => {
    try {
      // Прокручиваем к форме
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      
      // Получаем полную информацию о модели включая API ключ
      const response = await axios.get(`/api/models/${model._id}`);
      const fullModel = response.data;
      
      setFormData({
        name: fullModel.name,
        provider: fullModel.provider,
        modelId: fullModel.modelId,
        apiKey: fullModel.apiKey || '',
        baseUrl: fullModel.baseUrl || '',
        description: fullModel.description || '',
        active: fullModel.active,
        maxTokens: fullModel.maxTokens || 2000,
        transportMode: fullModel.transportMode || 'auto',
        reasoningEffort: fullModel.reasoningEffort || '',
        verbosity: fullModel.verbosity || '',
        isSystem: fullModel.isSystem || false,
        category: fullModel.category || 'chat',
        siteUrl: fullModel.siteUrl || '',
        siteName: fullModel.siteName || ''
      });
      
      setCurrentId(model._id);
      setIsEditing(true);
      setShowAddForm(true);
    } catch (err) {
      console.error('Ошибка при загрузке модели:', err);
      toast.error('Не удалось загрузить данные модели');
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту модель API?')) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(`/api/models/${id}`);
      setModels(models.filter(model => model._id !== id));
      toast.success('Модель успешно удалена');
    } catch (err) {
      console.error('Ошибка при удалении модели:', err);
      const errorMessage = err.response?.data?.message || 'Неизвестная ошибка';
      setError(`Не удалось удалить модель: ${errorMessage}`);
      toast.error(`Ошибка: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleApiKeyVisibility = (modelId) => {
    setShowApiKey(prev => ({
      ...prev,
      [modelId]: !prev[modelId]
    }));
  };

  const renderProviderBadge = (provider) => {
    const colors = {
      'openai': 'primary',
      'openrouter': 'success', 
      'anthropic': 'warning',
      'deepseek': 'info',
      'other': 'secondary'
    };
    return <span className={`badge bg-${colors[provider] || 'secondary'}`}>{provider}</span>;
  };

  const renderCategoryBadge = (category) => {
    const colors = {
      'chat': 'primary',
      'transcription': 'warning',
      'embedding': 'info',
      'image': 'success',
      'other': 'secondary'
    };
    return <span className={`badge bg-${colors[category] || 'secondary'}`}>{category}</span>;
  };

  const renderTransportBadge = (transportMode) => {
    const colors = {
      auto: 'primary',
      responses: 'success',
      chat_completions: 'warning'
    };

    return (
      <span className={`badge bg-${colors[transportMode] || 'secondary'}`}>
        {transportMode || 'auto'}
      </span>
    );
  };

  const currentModels = activeTab === 'user' ? models : systemModels;
  const isOpenAIProvider = formData.provider === 'openai';
  const effectiveTransportMode = isOpenAIProvider ? (formData.transportMode || 'auto') : 'auto';
  const supportsResponsesControls = isOpenAIProvider && supportsOpenAIResponsesControls(formData.modelId);
  const allowedReasoningEfforts = getAllowedOpenAIReasoningEfforts(formData.modelId);
  const supportsVerbosity = isOpenAIProvider && supportsOpenAIVerbosity(formData.modelId);
  const showResponsesControls = isOpenAIProvider && effectiveTransportMode !== 'chat_completions' && supportsResponsesControls;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <FontAwesomeIcon icon={faCog} className="me-2" />
          Управление моделями API
        </h1>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
            if (!showAddForm && formRef.current) {
              setTimeout(() => formRef.current.scrollIntoView({ behavior: 'smooth' }), 100);
            }
          }}
        >
          <FontAwesomeIcon icon={showAddForm ? faTimes : faPlus} className="me-2" />
          {showAddForm ? 'Отменить' : 'Добавить модель'}
        </button>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      {/* Табы для переключения между пользовательскими и системными моделями */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'user' ? 'active' : ''}`}
            onClick={() => setActiveTab('user')}
          >
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Пользовательские модели ({models.length})
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            <FontAwesomeIcon icon={faRobot} className="me-2" />
            Системные модели ({systemModels.length})
          </button>
        </li>
      </ul>
      
      {/* Форма добавления/редактирования модели */}
      {showAddForm && (
        <div className="card mb-4" ref={formRef}>
          <div className="card-header">
            <h5 className="mb-0">{isEditing ? 'Редактировать модель' : 'Добавить новую модель'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="name" className="form-label">Название модели</label>
                  <input 
                    type="text" 
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="col-md-6">
                  <label htmlFor="provider" className="form-label">Провайдер</label>
                  <select 
                    className="form-select"
                    id="provider"
                    name="provider"
                    value={formData.provider}
                    onChange={handleChange}
                    required
                  >
                    <option value="openai">OpenAI</option>
                    <option value="openrouter">OpenRouter</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="other">Другой</option>
                  </select>
                </div>
                
                <div className="col-md-6">
                  <label htmlFor="modelId" className="form-label">ID модели</label>
                  <input 
                    type="text" 
                    className="form-control"
                    id="modelId"
                    name="modelId"
                    value={formData.modelId}
                    onChange={handleChange}
                    placeholder="Например: gpt-4o-mini или openai/gpt-4o"
                    required
                  />
                </div>
                
                <div className="col-md-6">
                  <label htmlFor="category" className="form-label">Категория</label>
                  <select 
                    className="form-select"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="chat">Чат</option>
                    <option value="transcription">Транскрипция</option>
                    <option value="embedding">Эмбеддинги</option>
                    <option value="image">Изображения</option>
                    <option value="other">Другое</option>
                  </select>
                </div>
                
                <div className="col-12">
                  <label htmlFor="apiKey" className="form-label">API Ключ</label>
                  <input 
                    type="password" 
                    className="form-control"
                    id="apiKey"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleChange}
                    placeholder={isEditing ? "Оставьте пустым чтобы не изменять" : "Введите API ключ"}
                    required={!isEditing}
                  />
                </div>
                
                {formData.provider === 'openrouter' && (
                  <>
                    <div className="col-md-6">
                      <label htmlFor="siteUrl" className="form-label">URL сайта (для рейтинга)</label>
                      <input 
                        type="url" 
                        className="form-control"
                        id="siteUrl"
                        name="siteUrl"
                        value={formData.siteUrl}
                        onChange={handleChange}
                        placeholder="https://yoursite.com"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label htmlFor="siteName" className="form-label">Название сайта</label>
                      <input 
                        type="text" 
                        className="form-control"
                        id="siteName"
                        name="siteName"
                        value={formData.siteName}
                        onChange={handleChange}
                        placeholder="Название вашего приложения"
                      />
                    </div>
                  </>
                )}
                
                <div className="col-md-6">
                  <label htmlFor="baseUrl" className="form-label">Базовый URL</label>
                  <input 
                    type="url" 
                    className="form-control"
                    id="baseUrl"
                    name="baseUrl"
                    value={formData.baseUrl}
                    onChange={handleChange}
                    placeholder="Оставьте пустым для стандартного"
                  />
                </div>
                
                <div className="col-md-6">
                  <label htmlFor="maxTokens" className="form-label">Максимум токенов</label>
                  <input 
                    type="number" 
                    className="form-control"
                    id="maxTokens"
                    name="maxTokens"
                    value={formData.maxTokens}
                    onChange={handleChange}
                    min="1"
                    max="100000"
                  />
                </div>

                {isOpenAIProvider && (
                  <div className="col-md-6">
                    <label htmlFor="transportMode" className="form-label">Transport</label>
                    <select
                      className="form-select"
                      id="transportMode"
                      name="transportMode"
                      value={formData.transportMode}
                      onChange={handleChange}
                    >
                      <option value="auto">auto</option>
                      <option value="responses">responses</option>
                      <option value="chat_completions">chat_completions</option>
                    </select>
                    <div className="form-text">
                      `auto` для OpenAI сейчас означает Responses API. `chat_completions` использует legacy path и для GPT-5 переключается на `max_completion_tokens`, чтобы не ломать generation.
                    </div>
                  </div>
                )}

                {isOpenAIProvider && effectiveTransportMode !== 'chat_completions' && !supportsResponsesControls && (
                  <div className="col-12">
                    <div className="alert alert-info mb-0">
                      Reasoning effort доступен для OpenAI GPT-5 и o-series reasoning models, а verbosity только для GPT-5. Для остальных OpenAI моделей Responses API используется без этих controls.
                    </div>
                  </div>
                )}

                {showResponsesControls && (
                  <div className="col-md-6">
                    <label htmlFor="reasoningEffort" className="form-label">Reasoning effort</label>
                    <select
                      className="form-select"
                      id="reasoningEffort"
                      name="reasoningEffort"
                      value={formData.reasoningEffort}
                      onChange={handleChange}
                    >
                      <option value="">По умолчанию модели</option>
                      {allowedReasoningEfforts.map((effort) => (
                        <option key={effort} value={effort}>{effort}</option>
                      ))}
                    </select>
                    <div className="form-text">
                      Применяется только к OpenAI Responses API и ограничен значениями, которые мы разрешаем для этой модели.
                    </div>
                  </div>
                )}

                {showResponsesControls && supportsVerbosity && (
                  <div className="col-md-6">
                    <label htmlFor="verbosity" className="form-label">Verbosity</label>
                    <select
                      className="form-select"
                      id="verbosity"
                      name="verbosity"
                      value={formData.verbosity}
                      onChange={handleChange}
                    >
                      <option value="">По умолчанию модели</option>
                      <option value="low">low</option>
                      <option value="medium">medium</option>
                      <option value="high">high</option>
                    </select>
                    <div className="form-text">
                      Применяется только к OpenAI GPT-5 Responses API и управляет детальностью ответа.
                    </div>
                  </div>
                )}
                
                <div className="col-12">
                  <label htmlFor="description" className="form-label">Описание</label>
                  <textarea 
                    className="form-control"
                    id="description"
                    name="description"
                    rows="2"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Описание модели или заметки"
                  />
                </div>
                
                <div className="col-12">
                  <div className="form-check form-check-inline">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="active"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="active">
                      Активна
                    </label>
                  </div>
                  
                  <div className="form-check form-check-inline">
                    <input 
                      className="form-check-input"
                      type="checkbox"
                      id="isSystem"
                      name="isSystem"
                      checked={formData.isSystem}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="isSystem">
                      Системная модель
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <button type="submit" className="btn btn-success me-2">
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  {isEditing ? 'Обновить' : 'Создать'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Список моделей */}
      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
        </div>
      ) : currentModels.length === 0 ? (
        <div className="alert alert-info">
          {activeTab === 'user' ? 'Нет пользовательских моделей' : 'Нет системных моделей'}
        </div>
      ) : (
        <div className="row">
          {currentModels.map((model) => (
            <div key={model._id} className="col-md-6 col-lg-4 mb-4">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">{model.name}</h6>
                    <div>
                      {renderProviderBadge(model.provider)}
                      {model.isSystem && (
                        <span className="badge bg-danger ms-1">СИСТЕМА</span>
                      )}
                      {model.active ? (
                        <span className="badge bg-success ms-1">Активна</span>
                      ) : (
                        <span className="badge bg-secondary ms-1">Неактивна</span>
                      )}
                    </div>
                  </div>
                  
                  {!model.isSystem && (
                    <div className="btn-group btn-group-sm">
                      <button 
                        className="btn btn-outline-primary"
                        onClick={() => handleEditClick(model)}
                        title="Редактировать"
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </button>
                      <button 
                        className="btn btn-outline-danger"
                        onClick={() => handleDelete(model._id)}
                        title="Удалить"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="card-body">
                  <div className="mb-2">
                    <small className="text-muted">Модель:</small>
                    <div className="fw-bold">{model.modelId}</div>
                  </div>
                  
                  <div className="mb-2">
                    <small className="text-muted">Категория:</small>
                    <div>{renderCategoryBadge(model.category)}</div>
                  </div>

                  {model.provider === 'openai' && (
                    <div className="mb-2">
                      <small className="text-muted">Transport:</small>
                      <div>{renderTransportBadge(model.transportMode || 'auto')}</div>
                    </div>
                  )}

                  {model.provider === 'openai' && supportsOpenAIResponsesControls(model.modelId) && model.reasoningEffort && (
                    <div className="mb-2">
                      <small className="text-muted">Reasoning effort:</small>
                      <div className="small">{model.reasoningEffort}</div>
                    </div>
                  )}

                  {model.provider === 'openai' && supportsOpenAIResponsesControls(model.modelId) && model.verbosity && (
                    <div className="mb-2">
                      <small className="text-muted">Verbosity:</small>
                      <div className="small">{model.verbosity}</div>
                    </div>
                  )}
                  
                  {model.baseUrl && (
                    <div className="mb-2">
                      <small className="text-muted">Базовый URL:</small>
                      <div className="small text-break">{model.baseUrl}</div>
                    </div>
                  )}
                  
                  <div className="mb-2">
                    <small className="text-muted d-flex align-items-center">
                      API Ключ:
                      <button 
                        className="btn btn-sm btn-link p-0 ms-1"
                        onClick={() => toggleApiKeyVisibility(model._id)}
                        title={showApiKey[model._id] ? "Скрыть" : "Показать"}
                      >
                        <FontAwesomeIcon icon={showApiKey[model._id] ? faEyeSlash : faEye} />
                      </button>
                    </small>
                    <div className="small font-monospace">
                      {showApiKey[model._id] ? model.apiKey : model.maskedApiKey}
                    </div>
                  </div>
                  
                  {model.description && (
                    <div className="mb-2">
                      <small className="text-muted">Описание:</small>
                      <div className="small">{model.description}</div>
                    </div>
                  )}
                  
                  <div className="mb-0">
                    <small className="text-muted">Макс. токенов:</small>
                    <div className="small">{model.maxTokens}</div>
                  </div>
                </div>
                
                <div className="card-footer text-muted small">
                  Создана: {new Date(model.createdAt).toLocaleDateString()}
                  {model.updatedAt && model.updatedAt !== model.createdAt && (
                    <div>Изменена: {new Date(model.updatedAt).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApiModels; 
